import React from 'react';
import { Card, CardContent, Typography, Box, useTheme, Tooltip, Skeleton } from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, Cell, Tooltip as RechartsTooltip } from 'recharts';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';

// --- No changes needed here. The interface is generic and controlled by props. ---
interface SummaryCardProps {
  title: string;
  amount: string;
  change?: string;
  changeType?: 'positive' | 'negative' | 'info';
  changeDescription?: string;
  icon?: React.ReactNode;
  textureImagePath: string;
  isLoading?: boolean;
  chartData?: { value: number }[];
  lastBarColor?: string;
}

// --- No changes needed here. This tooltip is part of the mini-chart display logic. ---
const MiniChartTooltip = ({ active, payload, coordinate }: any) => {
    if (active && payload && payload.length) {
        return (
            <Box
                sx={{
                    position: 'absolute',
                    top: coordinate.y,
                    left: coordinate.x,
                    transform: 'translate(-50%, -120%)',
                    pointerEvents: 'none',
                    bgcolor: 'common.white',
                    p: '2px 8px',
                    borderRadius: '6px',
                    boxShadow: 3,
                }}
            >
                <Typography variant="caption" sx={{ fontFamily: 'Inter', fontWeight: 500 }}>
                    {payload[0].value.toLocaleString()}
                </Typography>
            </Box>
        );
    }
    return null;
};

const SummaryCard: React.FC<SummaryCardProps> = ({
    title,
    amount,
    change,
    changeType,
    changeDescription,
    icon,
    textureImagePath,
    isLoading,
    chartData,
    lastBarColor
}) => {
  const theme = useTheme();

  // --- No changes needed here. This logic formats the `change` prop for display. ---
  const isPositive = changeType === 'positive';
  const changeColor = changeType === 'positive' || changeType === 'info' ? '#03A65A' : '#CA2B20';

  let formattedChange = change;
  if (change) {
    const valueWithoutSign = String(change).replace(/^[+-]+/, '');
    if (changeType === 'negative') {
      formattedChange = `-${valueWithoutSign}`;
    } else if (changeType === 'positive') {
      formattedChange = `+${valueWithoutSign}`;
    }
  }

  // --- No changes needed here. This dynamically selects the correct icon. ---
  const ChangeIndicatorIcon = () => {
    if (changeType === 'info') {
      return <AccountBalanceWalletOutlinedIcon sx={{ fontSize: '0.80rem', mr: 0.5, color: changeColor }} />;
    }
    const ChangeArrowComponent = isPositive ? ArrowDownward : ArrowUpward;
    const rotationTransform = isPositive ? 'rotate(120deg)' : 'rotate(-120deg)';
    return <ChangeArrowComponent sx={{ fontSize: '1rem', color: changeColor, transform: rotationTransform }} />;
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '15px',
        height: '100%',
        minHeight: '130px',
        backgroundColor: '#F2F7FF',
        position: 'relative',
        ...(textureImagePath && {
          backgroundImage: `url(${process.env.PUBLIC_URL + textureImagePath})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'cover'
        }),
      }}
    >
      <CardContent sx={{ position: 'relative', zIndex: 1, p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        {/* Top Section: Displays title and amount. Already handles the `isLoading` prop. */}
        <Box>
            <Typography color='#666666' sx={{ fontWeight: '500', fontFamily: 'Inter',fontSize:'14px' }}>{title}</Typography>
            {isLoading ? (
                <Skeleton variant="text" width="60%" height={32} sx={{mt: 1}}/>
            ) : (
                <Tooltip title={amount} arrow>
                    <Typography component="p" sx={{ fontFamily: 'Inter', fontSize:'18px', fontWeight: '600', color: '#333333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 1 }}>
                    {amount && amount.startsWith('$') ? `${amount.charAt(0)}\u00A0${amount.slice(1)}` : amount}
                    </Typography>
                </Tooltip>
            )}
        </Box>

        {/* Bottom Section: Displays change indicator and mini-chart. Also handles `isLoading`. */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            {isLoading ? (
                <Skeleton variant="rounded" width={150} height={24} />
            ) : (
                change && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: theme.palette.common.white, borderRadius: '15px', px: 0.50, py: 0.25, maxWidth: '100%' }}>
                            <ChangeIndicatorIcon />
                            <Typography variant="caption" sx={{ color: changeColor, fontWeight: 'medium', lineHeight: 'normal' }}>
                                {formattedChange}
                            </Typography>
                            {changeDescription && (
                                <Tooltip title={changeDescription} arrow>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75, lineHeight: 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        • vs last month
                                    </Typography>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                )
            )}

            {/* The mini-chart, driven entirely by props. */}
            {chartData && chartData.length > 0 && (
                // **CHANGE**: Increased height of the container to make bars appear taller.
                <Box sx={{ width: '80px', height: '40px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                            <RechartsTooltip
                                cursor={false}
                                content={<MiniChartTooltip />}
                                wrapperStyle={{ zIndex: 10 }}
                            />
                            {/* **CHANGE**: Added barSize to make bars thinner. */}
                            <Bar dataKey="value" barSize={13}>
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={index === chartData.length - 1 ? lastBarColor : '#E5EFFF'}
                                        radius={2}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default SummaryCard;